import { useAuth0 } from '@auth0/auth0-react'
import { useApi } from './api'
import { useEffect, useState, createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import type { User, Role } from '../types'
import UnauthorizedPage from '../pages/UnauthorizedPage'

const DEV_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'

const DEV_USER: User = {
  id: 0,
  email: 'user@test.com',
  name: 'Test User',
  roles: ['admin'],
}

// ---------------------------------------------------------------------------
// canAccess – which sections are visible for which roles
// ---------------------------------------------------------------------------

export type Section = 'orders' | 'clients' | 'invoices' | 'flags' | 'users' | 'archive' | 'management'

export function canAccess(user: User | null, section: Section): boolean {
  if (!user) return false
  const { roles } = user
  if (roles.includes('admin')) return true
  if (section === 'orders' || section === 'archive')
    return roles.some(r => (['sales', 'fulfillment', 'design', 'accounting'] as Role[]).includes(r))
  if (section === 'clients') return roles.includes('sales')
  if (section === 'invoices') return roles.includes('accounting')
  if (section === 'flags')   return roles.some(r => (['sales', 'design'] as Role[]).includes(r))
  return false // 'users' – admin only (already handled above)
}

// ---------------------------------------------------------------------------
// UserContext – share the current user across the component tree
// ---------------------------------------------------------------------------

interface UserContextValue {
  user: User | null
  loading: boolean
}

const UserContext = createContext<UserContextValue>({ user: null, loading: true })

export function UserProvider({ children }: { children: ReactNode }) {
  const auth0 = DEV_BYPASS ? null : useAuth0() // eslint-disable-line react-hooks/rules-of-hooks
  const { user, loading, error } = useCurrentUser()

  if (!DEV_BYPASS && !loading && error === 'USER_NOT_PROVISIONED') {
    return <UnauthorizedPage email={auth0?.user?.email ?? ''} />
  }

  if (!DEV_BYPASS && !loading && error) {
    return (
      <div style={{ padding: 48, textAlign: 'center', fontFamily: 'sans-serif' }}>
        <h2 style={{ marginBottom: 12 }}>Authentifizierungsfehler</h2>
        <p style={{ color: '#ef4444', fontFamily: 'monospace', marginBottom: 24 }}>{error}</p>
        <p style={{ color: '#6b7280', fontSize: 14 }}>
          Bitte überprüfe deine Anmeldedaten oder wende dich an einen Administrator.
        </p>
      </div>
    )
  }

  return <UserContext.Provider value={{ user, loading }}>{children}</UserContext.Provider>
}

export function useUser(): UserContextValue {
  return useContext(UserContext)
}

// ---------------------------------------------------------------------------
// useCurrentUser – fetches user profile from /api/users/me
// ---------------------------------------------------------------------------

/**
 * Returns the current user's profile including their app roles
 * fetched from the /api/users/me endpoint.
 */
export function useCurrentUser() {
  // In dev bypass mode skip Auth0 entirely and return the hardcoded dev user.
  const auth0 = DEV_BYPASS ? null : useAuth0() // eslint-disable-line react-hooks/rules-of-hooks
  const { apiFetch } = useApi()
  const [user, setUser] = useState<User | null>(DEV_BYPASS ? DEV_USER : null)
  const [loading, setLoading] = useState(!DEV_BYPASS)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (DEV_BYPASS) return
    if (auth0!.isLoading || !auth0!.isAuthenticated) return
    apiFetch<User>('/api/users/me')
      .then(setUser)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Unknown error'),
      )
      .finally(() => setLoading(false))
  }, [auth0?.isLoading, auth0?.isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  return { user, loading, error }
}
